import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react'
import { useMounted } from '../components/layout'
import { toast } from '../components/toasts'
import { settingsWords as words } from './words'

/**
 * A line of text that is saved as she leaves it, or presses Return: no Save
 * button stands beside it. Escape puts back what is saved. An empty field
 * saves nothing and shows the saved text again. What the engine refuses is
 * said under the field, in its words, and what she typed stays.
 */
export function SavedText({ label, value, save, failed, maxLength = 100, placeholder, disabled = false }: {
  label: string
  /** What is saved now. */
  value: string
  save: (next: string) => Promise<unknown>
  /** What to say when the save fails with no sentence of the engine's. */
  failed: string
  maxLength?: number
  placeholder?: string
  disabled?: boolean
}) {
  const mounted = useMounted()
  const note = useId()
  const [draft, setDraft] = useState(value)
  const saving = useRef(false)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  // What is saved changed under the field (her own save's answer, another Mac): the field follows it.
  const held = useRef(value)
  useEffect(() => {
    if (held.current === value) return
    held.current = value
    setDraft(value)
  }, [value])

  async function commit() {
    const next = draft.trim()
    if (saving.current || disabled) return
    if (!next || next === value) {
      setDraft(value)
      setProblem(null)
      return
    }
    saving.current = true
    setBusy(true)
    setProblem(null)
    try {
      await save(next)
      if (mounted.current) toast(words.saved)
    } catch (error) {
      if (mounted.current) setProblem(error instanceof Error && error.message ? error.message : failed)
    } finally {
      saving.current = false
      if (mounted.current) setBusy(false)
    }
  }

  function keys(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter') {
      event.preventDefault()
      void commit()
    } else if (event.key === 'Escape' && draft !== value) {
      // The sheet closes on Escape; with something typed, Escape first takes it back.
      event.preventDefault()
      setDraft(value)
      setProblem(null)
    }
  }

  return <>
    <label className="field">
      <span>{label}</span>
      <input value={draft} maxLength={maxLength} placeholder={placeholder} readOnly={busy} disabled={disabled} autoComplete="off"
        aria-invalid={problem ? true : undefined} aria-describedby={problem ? note : undefined}
        onChange={event => { setDraft(event.target.value); setProblem(null) }} onBlur={() => void commit()} onKeyDown={keys} />
    </label>
    {problem && <p id={note} role="alert" className="text-danger">{problem}</p>}
  </>
}
